import ctl from '@netlify/classnames-template-literals'
import {
	Button,
	Glyphs,
	LiveField,
	MiniTabbedWidgetWrapper,
	useInputSliderPercentageAdapter,
	WidgetInputSliderField,
	WidgetMicroButton,
	WidgetWrapper,
} from '../../../rapid-ui'

import { useState } from 'react'
import { WidgetFontSelectDropdown } from '../../../constructs'
import { MetaTextLayerConfiguration, MetaTransformConfiguration } from '../../../views'
import { ValidFocusTarget } from '../../state'
import { Editable, EditableToolbarArgs } from './editable'

type EditableComplexTextProps =
	& {
		className?: string
		innerClassName?: string

		focusTargetId: ValidFocusTarget
		transform: MetaTransformConfiguration
		textLayer: MetaTextLayerConfiguration

		onTransformChange: (transform: MetaTransformConfiguration) => void
		onTextLayerChange: (layer: MetaTextLayerConfiguration) => void

		isGenerating: boolean
	}
	& ({
		overrideValue: true
		value: string
		onChange: (value: string) => void
	} | {
		overrideValue: false
	})

/**
 * Editable text in the ad simulators. Used for fully user-customizable text.
 */
export function EditableComplexText(props: EditableComplexTextProps) {
	const {
		className,
		innerClassName,
		transform,
		textLayer,
		isGenerating,
		onTransformChange,
		onTextLayerChange,
	} = props

	const content = props.overrideValue ? props.value : textLayer.content

	return (
		<Editable
			className={className}
			focusTargetId={props.focusTargetId}
			isGenerating={isGenerating}
			transform={transform}
			onTransformChange={onTransformChange}
			enableScaling={false}
			// temporarily disabled for uniformity; makes sense to re-enable once all elements use this
			// renderOverlay={({ enableTransform }) => (!enableTransform ? <HighlightOverlay /> : null)}
			renderToolbar={({ ...toolbarArgs }) => (
				toolbarArgs.enableTransform
					? <TransformModeToolbar {...props} {...toolbarArgs} />
					: <Toolbar {...props} {...toolbarArgs} />
			)}
		>
			{({ disabled }) => (
				<LiveField
					className={ctl(`block
                                relative resize-none
                                max-w-full
                                border-none border-transparent
                                focus:border-transparent
                                focus-visible:border-transparent
                                focus:outline-none
                                focus-visible:outline-none
                                focus-visible:ring-0
                                focus:ring-0
								${innerClassName}
                                `)}
					style={{
						fontSize: `${textLayer.sizeMultiplier * 100}%`,
						fontWeight: textLayer.fontWeight,
						fontFamily: textLayer.fontFamilyOverride ?? 'inherit',
						textShadow:
							'0 0 2px rgba(0, 0, 0, 0.7), 0 0 8px rgba(0, 0, 0, 0.3), 0 0 16px rgba(0, 0, 0, 0.6)',
					}}
					value={content}
					onChange={(value) => {
						props.overrideValue && props.onChange(value)
					}}
					disabled={disabled}
				/>
			)}
		</Editable>
	)
}

function TransformModeToolbar(
	props: EditableComplexTextProps & EditableToolbarArgs,
) {
	const { enableTransform, setEnableTransform, resetTransform } = props

	return (
		<WidgetWrapper padding={false}>
			<div className="flex items-center gap-2">
				<Button size="tiny" type="committal" onClick={() => setEnableTransform(false)}>Done</Button>
				<Button size="tiny" type="subtle" onClick={resetTransform}>Reset</Button>
			</div>
		</WidgetWrapper>
	)
}

function Toolbar(
	props: EditableComplexTextProps & EditableToolbarArgs,
) {
	const {
		textLayer,
		onTextLayerChange,
		enableTransform,
		setEnableTransform,
	} = props

	const sizeMultiplierSlider = useInputSliderPercentageAdapter({
		value: textLayer.sizeMultiplier,
		onChange: (value: number) => onTextLayerChange({ ...textLayer, sizeMultiplier: value }),
		min: 0.1,
		max: 2,
		step: 0.01,
	})

	const [tab, setTab] = useState<'text' | 'transform'>('text')

	return (
		<div className="flex items-center gap-2 min-w-[200px] max-w-[300px]">
			<MiniTabbedWidgetWrapper
				value={tab}
				onChange={setTab}
				tabs={[{
					id: 'text',
					icon: <Glyphs.core.TextIcon />,
					label: 'Text',
					children: (
						<>
							<div className="grid grid-cols-[auto_1fr] gap-2 items-center">
								<Glyphs.core.FontSizeIcon />
								<div className="grid grid-cols-[1fr_auto] items-center gap-2">
									<WidgetInputSliderField {...sizeMultiplierSlider} />
									<WidgetMicroButton
										onClick={() => onTextLayerChange({ ...textLayer, sizeMultiplier: 1 })}
									>
										<Glyphs.micro.ResetIcon />
									</WidgetMicroButton>
								</div>

								<Glyphs.core.WeightIcon />
								<WidgetInputSliderField
									value={textLayer.fontWeight}
									onChange={(value: number) => onTextLayerChange({ ...textLayer, fontWeight: value })}
									min={100}
									max={900}
									step={100}
								/>

								<Glyphs.core.FontFamilyIcon />
								<WidgetFontSelectDropdown
									value={textLayer.fontFamilyOverride}
									onChange={(value: string) =>
										onTextLayerChange({ ...textLayer, fontFamilyOverride: value })}
									allowDefault={true}
								/>
							</div>
						</>
					),
				}, {
					id: 'transform',
					icon: <Glyphs.core.TransformIcon />,
					label: 'Transform',
					children: (
						<>
							<Button size="tiny" type="subtle" onClick={() => setEnableTransform(true)}>
								Transform
							</Button>
							<div className="text-xs text-grey-500 mt-1">
								<div className="inline-block mr-1">
									<Glyphs.micro.InfoIcon />
								</div>
								Transformations are in alpha, and may change in the future.
							</div>
						</>
					),
				}]}
			/>
		</div>
	)
}
